<script setup lang="ts">
import InputError from '@/components/InputError.vue';
import InputLabel from '@/components/InputLabel.vue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ref } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useUpdateProfile } from '@/composables/api/useProfileApi';
import { AxiosError } from 'axios';

interface FormErrors {
  name?: string[];
  email?: string[];
  [key: string]: string[] | undefined;
}

const { userData } = useAuthStore();
const { mutate: updateProfile, isPending: isUpdatingProfile } = useUpdateProfile();

const errors = ref<FormErrors>({});
const name = ref<string>(userData?.name ?? '');
const email = ref<string>(userData?.email ?? '');

function updateProfileInformation() {
  updateProfile(
    {
      name: name.value,
      email: email.value
    },
    {
      onSuccess: () => {
        errors.value = {};
      },
      onError: (error: unknown) => {
        if (error instanceof AxiosError && error.response?.status === 422) {
          errors.value = error.response.data.errors;
        } else {
          errors.value = {};
        }
      }
    }
  );
}
</script>

<template>
  <section>
    <header>
      <h2 class="text-lg font-medium">Profile Information</h2>

      <p class="mt-1 text-sm">
        Update your account's profile information and email address.
      </p>
    </header>

    <form class="mt-6 space-y-6" @submit.prevent="updateProfileInformation">
      <div>
        <InputLabel for="name" value="Name" />

        <Input 
          id="name" 
          type="text" 
          class="mt-1 w-full" 
          required 
          :modelValue="name"
          @update:modelValue="(value: string | number) => name = String(value)"
          :disabled="isUpdatingProfile"
        />

        <InputError v-if="errors['name']" :message="errors['name'][0]" class="mt-2" />
      </div>

      <div>
        <InputLabel for="email" value="Email" />

        <Input 
          id="email" 
          type="email" 
          class="mt-1 w-full" 
          required 
          :modelValue="email"
          @update:modelValue="(value: string | number) => email = String(value)"
          :disabled="isUpdatingProfile"
        />

        <InputError v-if="errors['email']" :message="errors['email'][0]" class="mt-2" />
      </div>

      <div class="flex items-center gap-4">
        <Button 
          type="submit"
          :disabled="isUpdatingProfile"
          :loading="isUpdatingProfile"
        >
          Save
        </Button>
      </div>
    </form>
  </section>
</template>
